import {
  getSupplierItemsIdPriceHistory,
  useGetStockItems,
  useGetSupplierComparisonStockItemId,
} from '@agos/api-client/inventory'
import { T, formatDateTime, tt, useModalFocus } from '@agos/shared'
import {
  DataTable,
  EmptyState,
  PageHeader,
  SearchInput,
  Spinner,
  StatCard,
  StatusBadge,
  cn,
  type Column,
} from '@agos/ui'
import { useQueries } from '@tanstack/react-query'
import {
  Award,
  History,
  Info,
  Scale,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import {
  byPrice,
  formatPercent,
  formatUnitPrice,
  isCheapest,
  parseRecordedAt,
  pickableItems,
  sixMonthAverage,
  spreadOf,
  trendOf,
  truncationNote,
  type ComparisonRow,
  type PricePoint,
  type StockItemOption,
} from './price-comparison'

/**
 * `/supplier-comparison` — who is cheapest for a stock item, and how the price moved.
 *
 * Pick an item, see one row per supplier that has a code mapped to it: the last unit
 * price, the six-month average, and its price history behind a click.
 *
 * **FB-081** is that the six-month average is always zero. The server's subquery
 * inner-joins `invoices` on `h.invoice_id`, and every price-history row in the restored
 * data has that NULL — so the join drops all of them and both the average and its count
 * come back 0, while five real prices sit in the table. Derived here from
 * `/supplier-items/{id}/price-history` and labelled as derived, the way FB-060 set out.
 *
 * Ported from legacy `pages/supplier-comparison.tsx`.
 */


export function SupplierComparisonPage() {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [historyFor, setHistoryFor] = useState<ComparisonRow | null>(null)

  // Read once for the visit. A six-month window does not move while somebody looks at
  // one item, and a value that changed every render could not be memoised on.
  const [now] = useState(() => Date.now())

  /* FB-842, §9.556 - **`isError`, because the picker's *Nič sa nenašlo* reads as a bad search.** There is a search box
     directly above that list, so a failed read made the operator retype - and nothing can be compared at all, since the
     right-hand panel is chosen from this one. */
  const {
    data: itemData,
    isLoading,
    isError: itemsFailed,
    refetch: refetchItems,
  } = useGetStockItems({ active: 'true' })
  const items = useMemo<StockItemOption[]>(() => itemData ?? [], [itemData])
  const pickable = useMemo(() => pickableItems(items, search), [items, search])
  const truncated = truncationNote(items, search)
  const selected = items.find((item) => item.id === selectedId) ?? null

  /* FB-842, §9.556 - **the instructional claim, which is FB-812's shape.** *Táto položka nemá u žiadneho dodávateľa
     namapovaný kód … Kódy sa nastavujú na stránke Dodávatelia* is two claims and a direction, so a failed read sent a
     buyer to map a code that is already there. */
  const {
    data: comparisonData,
    isFetching,
    isError: comparisonFailed,
    refetch: refetchComparison,
  } = useGetSupplierComparisonStockItemId(
    selectedId ?? 0,
    { query: { enabled: selectedId !== null } },
  )
  const rows = useMemo<ComparisonRow[]>(() => comparisonData ?? [], [comparisonData])

  /**
   * The price history for every supplier of the chosen item, at once.
   *
   * Needed because the six-month average has to be derived from it — one request per
   * row, which for the widest item in the data is two.
   */
  const histories = useQueries({
    queries: rows.map((row) => ({
      queryKey: ['/api/v1/supplier-items', row.supplierItemCodeId, 'price-history'],
      queryFn: () => getSupplierItemsIdPriceHistory(row.supplierItemCodeId ?? 0),
      enabled: row.supplierItemCodeId !== undefined,
      staleTime: 60_000,
    })),
  })

  const historyById = useMemo(() => {
    const byId = new Map<number, PricePoint[]>()
    rows.forEach((row, index) => {
      const points = histories[index]?.data
      if (row.supplierItemCodeId !== undefined && Array.isArray(points)) {
        byId.set(row.supplierItemCodeId, points)
      }
    })
    return byId
  }, [rows, histories])

  const sorted = byPrice(rows)
  const spread = spreadOf(rows)
  const anyDerived = sorted.some(
    (row) => sixMonthAverage(row, historyById.get(row.supplierItemCodeId ?? -1), now).derived,
  )

  const columns: Column<ComparisonRow>[] = useMemo(
    () => [
      {
        key: 'supplier',
        header: tt('Dodávateľ'),
        sortable: true,
        sortValue: (row) => row.supplierName ?? '',
        render: (row) => (
          <div className="flex items-center gap-2 min-w-0">
            {isCheapest(row, rows) && <Award className="w-4 h-4 text-emerald-500 shrink-0" />}
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{row.supplierName}</p>
              <p className="text-[11px] text-muted-foreground font-mono truncate">
                {row.supplierCode}
              </p>
            </div>
          </div>
        ),
      },
      {
        key: 'lastUnitPrice',
        header: tt('Posledná cena'),
        sortable: true,
        sortValue: (row) => row.lastUnitPrice ?? Number.POSITIVE_INFINITY,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (row) => (
          <span
            className={cn(
              'font-bold tabular-nums whitespace-nowrap',
              isCheapest(row, rows) && 'text-emerald-500',
            )}
          >
            {formatUnitPrice(row.lastUnitPrice)}
            {row.unit ? <span className="text-muted-foreground"> / {row.unit}</span> : null}
          </span>
        ),
      },
      {
        // FB-081 — the server sends 0 here; this is worked out from the history.
        key: 'average',
        header: tt('Priemer 6 mes.'),
        sortable: true,
        sortValue: (row) =>
          sixMonthAverage(row, historyById.get(row.supplierItemCodeId ?? -1), now).averagePrice ??
          Number.POSITIVE_INFINITY,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (row) => {
          const average = sixMonthAverage(row, historyById.get(row.supplierItemCodeId ?? -1), now)
          if (average.averagePrice === null) {
            return <span className="text-xs text-muted-foreground">-</span>
          }
          return (
            <span className="text-xs tabular-nums whitespace-nowrap">
              {formatUnitPrice(average.averagePrice)}
              <span className="text-muted-foreground"> · {average.count}×</span>
            </span>
          )
        },
      },
      {
        key: 'trend',
        header: tt('Trend'),
        render: (row) => {
          const average = sixMonthAverage(row, historyById.get(row.supplierItemCodeId ?? -1), now)
          const trend = trendOf(row.lastUnitPrice, average.averagePrice)
          if (trend === 'unknown') return <span className="text-xs text-muted-foreground">-</span>
          if (trend === 'flat') {
            return <StatusBadge color="zinc" label={tt('bez zmeny')} />
          }
          return trend === 'down' ? (
            <StatusBadge color="green" label={tt('klesá')} icon={TrendingDown} />
          ) : (
            <StatusBadge color="red" label={tt('stúpa')} icon={TrendingUp} />
          )
        },
      },
      {
        key: 'lastInvoice',
        header: tt('Posledná faktúra'),
        render: (row) =>
          row.lastInvoiceNumber ? (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {row.lastInvoiceNumber}
              {row.lastInvoiceDate ? ` · ${row.lastInvoiceDate}` : ''}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          ),
      },
      {
        key: 'actions',
        header: '',
        render: (row) => (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setHistoryFor(row)}
              aria-label={`${tt('Vývoj ceny')} - ${row.supplierName ?? ''}`}
              title={tt('Vývoj ceny')}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <History className="w-3.5 h-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [rows, historyById, now],
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Porovnanie dodávateľov')}
        description={tt('Kto je najlacnejší pre danú skladovú položku a ako sa cena hýbala')}
        icon={<Scale className="w-5 h-5" />}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        <div className="space-y-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={tt('Hľadať skladovú položku...')}
          />
          {truncated && <p className="text-[11px] text-muted-foreground">{truncated}</p>}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : itemsFailed ? (
            /* Before the list, whose own *Nič sa nenašlo* is right about a search that matched nothing. */
            <div className="border border-destructive/40 rounded-xl p-6" data-item-picker-error>
              <EmptyState
                icon={<Scale className="w-10 h-10" />}
                title={tt('Položky sa nepodarilo načítať')}
                description={tt('Toto nie je prázdny sklad - server neodpovedal, takže sa nedá vybrať čo porovnávať.')}
                action={
                  <button type="button" onClick={() => void refetchItems()} data-item-picker-retry className="px-2.5 py-1 rounded-lg border border-border font-semibold text-foreground text-sm">
                    <T>Skúsiť znova</T>
                  </button>
                }
              />
            </div>
          ) : (
            <div
              className="border border-border rounded-xl divide-y divide-border max-h-[28rem] overflow-y-auto"
              data-item-picker
            >
              {pickable.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedId(item.id ?? null)}
                  aria-pressed={selectedId === item.id}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm transition-colors',
                    selectedId === item.id
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'hover:bg-muted/50',
                  )}
                >
                  <span className="truncate block">{item.name}</span>
                  <span className="text-[11px] text-muted-foreground">{item.unit}</span>
                </button>
              ))}
              {pickable.length === 0 && (
                <p className="px-3 py-6 text-sm text-muted-foreground text-center">
                  <T>Nič sa nenašlo</T>
                </p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {selectedId === null ? (
            <div className="border border-border rounded-2xl">
              <EmptyState
                icon={<Scale className="w-10 h-10" />}
                description={tt('Vyberte skladovú položku vľavo.')}
              />
            </div>
          ) : isFetching && rows.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : comparisonFailed ? (
            /* Before the *no code mapped anywhere* sentence, because `rows` is empty on a failure too and that sentence
               is an instruction rather than a description. */
            <div className="border border-destructive/40 rounded-2xl" data-comparison-error>
              <EmptyState
                icon={<Scale className="w-10 h-10" />}
                title={tt('Porovnanie sa nepodarilo načítať')}
                description={tt('Nevieme, u koho je táto položka namapovaná - server neodpovedal. Kódy nie je treba nastavovať.')}
                action={
                  <button type="button" onClick={() => void refetchComparison()} data-comparison-retry className="px-2.5 py-1 rounded-lg border border-border font-semibold text-foreground text-sm">
                    <T>Skúsiť znova</T>
                  </button>
                }
              />
            </div>
          ) : rows.length === 0 ? (
            <div className="border border-border rounded-2xl">
              <EmptyState
                icon={<Scale className="w-10 h-10" />}
                title={selected?.name}
                description={tt(
                  'Táto položka nemá u žiadneho dodávateľa namapovaný kód, takže nie je čo porovnávať. Kódy sa nastavujú na stránke Dodávatelia.',
                )}
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-comparison-totals>
                <StatCard
                  label={tt('Dodávateľov')}
                  value={String(rows.length)}
                  icon={Scale}
                  color="blue"
                />
                <StatCard
                  label={tt('Najlacnejšie')}
                  value={formatUnitPrice(spread?.minPrice ?? sorted[0]?.lastUnitPrice)}
                  icon={Award}
                  color="emerald"
                  sub={spread?.cheapest.supplierName ?? sorted[0]?.supplierName}
                />
                <StatCard
                  label={tt('Rozdiel')}
                  value={spread ? formatPercent(spread.percent) : '-'}
                  icon={TrendingUp}
                  color={spread && spread.percent > 10 ? 'red' : 'violet'}
                  sub={spread ? spread.dearest.supplierName : undefined}
                />
              </div>

              {/* Not computed from one supplier: legacy shows 0 %, which reads as
                  "they all charge the same". */}
              {!spread && rows.length > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <Info className="w-4 h-4 shrink-0 mt-px" />
                  <T>
                    Túto položku má nacenenú len jeden dodávateľ, takže nie je s čím ju porovnať.
                  </T>
                </p>
              )}

              {spread && spread.percent > 10 && (
                <p className="flex items-start gap-1.5 text-xs text-amber-500" data-spread-warning>
                  <TrendingUp className="w-4 h-4 shrink-0 mt-px" />
                  <span>
                    <strong>{spread.dearest.supplierName}</strong> <T>je o</T>{' '}
                    <strong>{formatPercent(spread.percent)}</strong> <T>drahší ako</T>{' '}
                    <strong>{spread.cheapest.supplierName}</strong>{' '}
                    <T>pri poslednej cene.</T>
                  </span>
                </p>
              )}

              {/* FB-081 - the figure is ours, and it says so. */}
              {anyDerived && (
                <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground" data-derived-note>
                  <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <T>
                    Priemer za 6 mesiacov je spočítaný z histórie cien - server ho vracia nulový,
                    pretože jeho výpočet vyžaduje faktúru, ktorú tieto záznamy nemajú.
                  </T>
                </p>
              )}

              <div data-comparison-rows>
                <DataTable<ComparisonRow>
                  columns={columns}
                  data={sorted}
                  rowKey={(row) => row.supplierItemCodeId ?? 0}
                  pageSize={25}
                  compact
                  emptyMessage={tt('Žiadni dodávatelia')}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {historyFor && (
        <PriceHistoryDialog
          row={historyFor}
          points={historyById.get(historyFor.supplierItemCodeId ?? -1)}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  )
}

function PriceHistoryDialog({
  row,
  points,
  onClose,
}: {
  row: ComparisonRow
  points: PricePoint[] | undefined
  onClose: () => void
}) {
  // Newest first — the price somebody is about to pay is the one at the top.
  const sorted = useMemo(
    () =>
      [...(points ?? [])].sort(
        (left, right) =>
          (parseRecordedAt(right.recordedAt) ?? 0) - (parseRecordedAt(left.recordedAt) ?? 0),
      ),
    [points],
  )

  /* §9.494, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. Wrapper is the dismiss scrim, so
     the role stays on it and the trap is bounded by the card inside (§9.469). */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${tt('Vývoj ceny')} - ${row.supplierName ?? ''}`}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-price-history
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <p className="font-bold text-sm truncate">{row.supplierName}</p>
            <p className="text-[11px] text-muted-foreground font-mono">{row.supplierCode}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {points === undefined ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : sorted.length === 0 ? (
            <EmptyState
              icon={<History className="w-10 h-10" />}
              description={tt('Pre tento kód nie je zaznamenaná žiadna cena.')}
            />
          ) : (
            <ul className="divide-y divide-border">
              {sorted.map((point, index) => {
                const previous = sorted[index + 1]
                const change =
                  previous?.unitPrice != null && point.unitPrice != null
                    ? point.unitPrice - previous.unitPrice
                    : null
                return (
                  <li key={point.id ?? index} className="py-2.5 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold tabular-nums">
                        {formatUnitPrice(point.unitPrice)}
                        {row.unit ? (
                          <span className="text-muted-foreground font-normal"> / {row.unit}</span>
                        ) : null}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {point.recordedAt ? formatDateTime(normaliseStamp(point.recordedAt)) : '-'}
                        {point.qty != null ? ` · ${point.qty} ${row.unit ?? ''}` : ''}
                        {point.invoiceNumber ? ` · ${point.invoiceNumber}` : ''}
                      </p>
                    </div>
                    {change !== null && Math.abs(change) >= 0.001 && (
                      <span
                        className={cn(
                          'text-xs font-bold tabular-nums shrink-0',
                          change > 0 ? 'text-red-500' : 'text-emerald-500',
                        )}
                      >
                        {change > 0 ? '+' : ''}
                        {formatUnitPrice(Math.abs(change)).replace(' €', '')}
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

/** `formatDateTime` wants something `Date` can read; Postgres does not oblige. */
function normaliseStamp(value: string): number {
  return parseRecordedAt(value) ?? 0
}
